import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, ShieldCheck, Shield, UserRound } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { usersApi, mydataError } from "@/api";
import { useLang, fmtDate } from "@/i18n";
import { useAuth } from "@/auth";
import { cn } from "@/lib/utils";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { UserDialog } from "@/components/UserDialog";

const ROLE_ICON = { superadmin: ShieldCheck, admin: Shield, user: UserRound };
const ROLE_STYLE = {
  superadmin: "text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-800",
  admin: "text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-950/40 border-blue-200 dark:border-blue-800",
  user: "text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-700",
};

export default function UsersPage() {
  const { t, lang } = useLang();
  const { user: me } = useAuth();
  const [rows, setRows] = useState([]);
  const [modules, setModules] = useState([]);
  const [editing, setEditing] = useState(undefined);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(() => { usersApi.list().then(setRows).catch(() => toast.error(t("error"))); }, [t]);
  useEffect(() => { load(); usersApi.modules().then(setModules); }, [load]);

  const remove = async () => {
    try { await usersApi.remove(deleting.id); toast.success(t("user_deleted")); load(); }
    catch (e) { toast.error(mydataError(e, t)); } finally { setDeleting(null); }
  };

  return (
    <div className="space-y-5 page-enter" data-testid="users-page">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-slate-500 max-w-2xl">{t("users_hint")}</p>
        <Button onClick={() => setEditing(null)} className="bg-blue-800 hover:bg-blue-700" data-testid="create-user-button"><Plus size={16} className="mr-1" />{t("new_user")}</Button>
      </div>
      <div className="erp-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm erp-table">
            <thead>
              <tr className="text-left">
                <th className="px-4 py-3">{t("username")}</th>
                <th className="px-4 py-3">{t("full_name")}</th>
                <th className="px-4 py-3">{t("role")}</th>
                <th className="px-4 py-3">{t("modules")}</th>
                <th className="px-4 py-3">{t("status")}</th>
                <th className="px-4 py-3 hidden md:table-cell">{t("created")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {rows.map((u) => {
                const Icon = ROLE_ICON[u.role];
                const isSuper = u.role === "superadmin";
                return (
                  <tr key={u.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40" data-testid={`user-row-${u.username}`}>
                    <td className="px-4 py-3 font-mono font-medium">{u.username}{u.id === me.id && <span className="ml-2 text-[10px] uppercase text-slate-400">{t("you")}</span>}</td>
                    <td className="px-4 py-3">{u.full_name || "—"}<div className="text-xs text-slate-500">{u.email}</div></td>
                    <td className="px-4 py-3"><span className={cn("inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs font-medium", ROLE_STYLE[u.role])}><Icon size={12} />{t(`role_${u.role}`)}</span></td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(u.modules || []).map((m) => <span key={m} className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-[11px]">{t(`nav_${m}`)}</span>)}
                      </div>
                    </td>
                    <td className="px-4 py-3"><span className={cn("text-xs font-semibold", u.active ? "text-emerald-700 dark:text-emerald-300" : "text-rose-600")} data-testid={`user-status-${u.username}`}>{u.active ? t("active") : t("inactive")}</span></td>
                    <td className="px-4 py-3 hidden md:table-cell font-mono text-slate-500">{fmtDate(u.created_at?.slice(0, 10), lang)}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        {(!isSuper || u.id === me.id) && <button onClick={() => setEditing(u)} data-testid={`user-edit-${u.username}`} className="h-8 w-8 inline-flex items-center justify-center rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500"><Pencil size={15} /></button>}
                        {me.role === "superadmin" && !isSuper && u.id !== me.id && <button onClick={() => setDeleting(u)} data-testid={`user-delete-${u.username}`} className="h-8 w-8 inline-flex items-center justify-center rounded hover:bg-rose-100 dark:hover:bg-rose-950 text-rose-600"><Trash2 size={15} /></button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <UserDialog open={editing !== undefined} user={editing} modules={modules} onClose={() => setEditing(undefined)} onSaved={() => { setEditing(undefined); load(); }} />
      <ConfirmDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)} message={t("confirm_delete_user")} onConfirm={remove} />
    </div>
  );
}
